import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Archive, ShieldCheck } from 'lucide-react';

const STAFF_ROLES = ['ADMIN', 'SNC', 'PHYSIO', 'COACH'];

/**
 * Bilingual reactivation dialog (iter_122).
 *
 * Opened when POST /users/invite returns HTTP 409 with
 *   { detail: { error: 'ARCHIVED_ACCOUNT_FOUND', archived_user: {...} } }.
 *
 * Props:
 *   - open, onClose
 *   - archivedUser: { id, first_name, last_name, role, last_team, archived_at }
 *   - teams: [{ id, code, name }]
 *   - api: axios instance (from useAuth)
 *   - lang: 'EN' | 'IT'
 *   - onReactivated: (payload) => void   // called on success
 */
export default function ReactivateAccountDialog({
  open, onClose, archivedUser, teams = [], api, lang = 'EN', onReactivated,
}) {
  const t = (en, it) => (lang === 'IT' ? it : en);
  const isAthlete = archivedUser?.role === 'ATHLETE';

  const [teamIds, setTeamIds] = useState([]);
  const [role, setRole] = useState(archivedUser?.role || 'COACH');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTeamIds([]);
      setRole(archivedUser?.role || 'COACH');
    }
  }, [open, archivedUser]);

  if (!archivedUser) return null;

  const archivedDate = archivedUser.archived_at
    ? new Date(archivedUser.archived_at).toLocaleDateString()
    : '—';

  const handleReactivate = async () => {
    if (isAthlete && teamIds.length === 0) {
      toast.error(t(
        'Select at least one team for the athlete.',
        'Seleziona almeno una squadra per l\'atleta.'
      ));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { team_ids: teamIds };
      if (teamIds.length > 0) payload.primary_team_id = teamIds[0];
      if (!isAthlete && role && role !== archivedUser.role) payload.role = role;
      const res = await api.post(`/users/${archivedUser.id}/reactivate`, payload);
      toast.success(t(
        'Account reactivated. Previous history has been preserved.',
        'Account riattivato. Lo storico precedente è stato mantenuto.'
      ));
      if (onReactivated) onReactivated(res.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reactivation failed';
      toast.error(typeof msg === 'string' ? msg : t('Reactivation failed', 'Riattivazione fallita'));
    } finally {
      setSubmitting(false);
    }
  };

  const canConfirm = !submitting && (!isAthlete || teamIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent
        className="bg-zinc-900 border-amber-500/30 max-w-md w-[95vw]"
        data-testid="reactivate-account-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-400" />
            {t('Archived account found', 'Account archiviato trovato')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-zinc-400 leading-relaxed" data-testid="reactivate-explainer">
            {t(
              'This email belongs to a previously archived account. You can reactivate the same person while preserving their previous history.',
              'Questa email appartiene a un account precedentemente archiviato. Puoi riattivare la stessa persona mantenendo tutto lo storico precedente.'
            )}
          </p>

          <div
            className="rounded border border-zinc-800 bg-zinc-950/70 p-3 space-y-1.5 text-sm"
            data-testid="reactivate-user-summary"
          >
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs">{t('Name', 'Nome')}</span>
              <span className="text-zinc-200 font-medium">
                {archivedUser.first_name} {archivedUser.last_name}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs">{t('Previous role', 'Ruolo precedente')}</span>
              <Badge className="text-[10px] bg-zinc-800 text-zinc-300 border-zinc-700">
                {archivedUser.role}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs">{t('Last team', 'Ultima squadra')}</span>
              <span className="text-zinc-300 text-xs">{archivedUser.last_team || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs">{t('Archived on', 'Data archiviazione')}</span>
              <span className="text-zinc-300 text-xs">{archivedDate}</span>
            </div>
          </div>

          {!isAthlete && (
            <div data-testid="reactivate-role-picker-wrap">
              <Label className="text-zinc-400 text-xs">
                {t('Current role', 'Ruolo attuale')}
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="reactivate-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {STAFF_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {role !== archivedUser.role && (
                <p className="text-[11px] text-amber-400 mt-1">
                  {t(
                    `Role will change from ${archivedUser.role} to ${role}. Historical attribution is preserved.`,
                    `Il ruolo cambierà da ${archivedUser.role} a ${role}. L'attribuzione storica sarà preservata.`
                  )}
                </p>
              )}
            </div>
          )}

          <div>
            <Label className="text-zinc-400 text-xs">
              {t('Current team(s)', 'Squadra/e attuale/i')}
              {isAthlete && <span className="ml-1.5 text-rose-400">*</span>}
            </Label>
            <div
              className={`mt-1 space-y-1 max-h-40 overflow-y-auto rounded-sm p-1 ${
                isAthlete && teamIds.length === 0 ? 'border border-rose-500/40' : 'border border-zinc-800'
              }`}
              data-testid="reactivate-team-picker"
            >
              {teams.length === 0 && (
                <p className="text-xs text-zinc-500 p-2">
                  {t('No teams available.', 'Nessuna squadra disponibile.')}
                </p>
              )}
              {teams.map((tm) => (
                <label
                  key={tm.id}
                  className="flex items-center gap-2 p-1.5 hover:bg-zinc-800/50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={teamIds.includes(tm.id)}
                    onChange={(e) => {
                      setTeamIds((prev) =>
                        e.target.checked ? [...prev, tm.id] : prev.filter((id) => id !== tm.id)
                      );
                    }}
                    className="rounded border-zinc-700"
                    data-testid={`reactivate-team-${tm.id}`}
                  />
                  <span className="text-sm text-zinc-300">
                    {tm.code} — {tm.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500/70" />
            {t(
              'Old team and historical records will remain untouched.',
              'La squadra storica e i dati passati non verranno modificati.'
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="border-zinc-700"
            onClick={onClose}
            disabled={submitting}
            data-testid="reactivate-cancel-btn"
          >
            {t('Cancel', 'Annulla')}
          </Button>
          <Button
            onClick={handleReactivate}
            disabled={!canConfirm}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            data-testid="reactivate-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-2" />
            )}
            {t('Reactivate account', 'Riattiva account')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
